import { isEmpty } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { useTrackCollectionFiltered } from '../../../app/_providers/TrackingProvider.client'
import { addOrRemoveItemFromList, isRecordEmpty, toggleItemInList } from '../../../lib/utils'
import { extractSelectedCollectionFilters } from '../../../lib/utils/collection-filters'
import { FILTERS_QUERY_PARAM, LegacyFilterQueryParam } from '../../constants'

import { UseQueryParams } from './CollectionFilterQueryParamsContext'
import { ICollectionFilterQueryParamsContext } from './types'

let lastTrackedFiltersString = ''

export function useCollectionFilterQueryParams({
  useQueryParamsImpl,
  collectionHandle,
}: {
  /** Implementation hook for reading & mutating the URL parameters. */
  useQueryParamsImpl: () => UseQueryParams
  collectionHandle: string | null
}): ICollectionFilterQueryParamsContext {
  const { trackCollectionFiltered } = useTrackCollectionFiltered()
  const { query, updateQueryParams } = useQueryParamsImpl()

  const collectionFiltersQueryParamAsString = `${query[FILTERS_QUERY_PARAM]}`
  const legacyCategoryQueryParamAsString = `${query[LegacyFilterQueryParam.Category]}`
  const legacyColorQueryParamAsString = `${query[LegacyFilterQueryParam.Color]}`
  const legacyFitQueryParamAsString = `${query[LegacyFilterQueryParam.Fit]}`

  const selectedFilters: Record<string, string[]> = useMemo(() => {
    // Force a dependency on these strings since they are safe things to trigger a recalc of the selected filters.
    // @ts-ignore
    const _forcedDependency =
      collectionFiltersQueryParamAsString +
      legacyCategoryQueryParamAsString +
      legacyColorQueryParamAsString +
      legacyFitQueryParamAsString

    const collectionFiltersQueryParam = query[FILTERS_QUERY_PARAM]
    const legacyCategoryQueryParam = query[LegacyFilterQueryParam.Category]
    const legacyColorQueryParam = query[LegacyFilterQueryParam.Color]
    const legacyFitQueryParam = query[LegacyFilterQueryParam.Fit]

    return extractSelectedCollectionFilters({
      collectionFiltersQueryParam,
      legacyCategoryQueryParam,
      legacyColorQueryParam,
      legacyFitQueryParam,
    })
  }, [
    query,
    collectionFiltersQueryParamAsString,
    legacyCategoryQueryParamAsString,
    legacyColorQueryParamAsString,
    legacyFitQueryParamAsString,
  ])

  const updateFilterSelection = useCallback(
    (key: string, value: string, selected: boolean) => {
      const updatedSelectedFilterValues: string[] = addOrRemoveItemFromList({
        item: value,
        list: selectedFilters[key] ?? [],
        operation: selected ? 'ADD' : 'REMOVE',
      })
      const updatedSelectedFilters = {
        ...selectedFilters,
      }
      if (updatedSelectedFilterValues.length > 0) {
        updatedSelectedFilters[key] = updatedSelectedFilterValues
      } else {
        delete updatedSelectedFilters[key]
      }
      updateQueryParams({
        [FILTERS_QUERY_PARAM]: isRecordEmpty(updatedSelectedFilters)
          ? undefined
          : JSON.stringify(updatedSelectedFilters),
        [LegacyFilterQueryParam.Category]: undefined,
        [LegacyFilterQueryParam.Color]: undefined,
        [LegacyFilterQueryParam.Fit]: undefined,
      })
    },
    [selectedFilters, updateQueryParams]
  )

  const toggleSelectedFilter = useCallback(
    (key: string, value: string) => {
      const updatedSelectedFilterValues: string[] = toggleItemInList(
        value,
        selectedFilters[key] ?? []
      )
      const updatedSelectedFilters = {
        ...selectedFilters,
      }
      if (updatedSelectedFilterValues.length > 0) {
        updatedSelectedFilters[key] = updatedSelectedFilterValues
      } else {
        delete updatedSelectedFilters[key]
      }
      updateQueryParams({
        [FILTERS_QUERY_PARAM]: isRecordEmpty(updatedSelectedFilters)
          ? undefined
          : JSON.stringify(updatedSelectedFilters),
        [LegacyFilterQueryParam.Category]: undefined,
        [LegacyFilterQueryParam.Color]: undefined,
        [LegacyFilterQueryParam.Fit]: undefined,
      })
    },
    [selectedFilters, updateQueryParams]
  )

  const clearAllFilters = useCallback(() => {
    updateQueryParams({
      [FILTERS_QUERY_PARAM]: undefined,
      [LegacyFilterQueryParam.Category]: undefined,
      [LegacyFilterQueryParam.Color]: undefined,
      [LegacyFilterQueryParam.Fit]: undefined,
    })
  }, [updateQueryParams])

  const totalNumberOfSelectedFilterValues: number = useMemo(
    () =>
      Object.values(selectedFilters).reduce(
        (count, filterValues) => count + filterValues.length,
        0
      ),
    [selectedFilters]
  )

  /**
   * An event like this would normally be coded to happen when someone calls the updateQueryParams handler.
   * I can't do that though because of the "crawlable" filters. Those don't work via calling updateQueryParams,
   * they are just links. So to put this code in one spot, I need it to be reactive to changes in `selectedFilters`.
   *
   * The other issue here is that this is not a Context provider. Its just a normal hook. So if its used multiple times
   * on a web page, it would cause multiple tracking sends for a single change. To resolve that without having to put this code
   * somewhere decoupled from this file which owns collection filtering, I hoisted lastTrackedFiltersString up to a shared location
   * outside this hook. That does mean during SSR it is shared across users page requests. But its only used in an effect, so its never
   * touched in SSR since effects are clientside.
   */
  useUpdateEffect(() => {
    if (!collectionHandle || isEmpty(selectedFilters)) return

    const selectedFiltersAsString = JSON.stringify(selectedFilters)
    if (lastTrackedFiltersString !== selectedFiltersAsString) {
      lastTrackedFiltersString = selectedFiltersAsString
      trackCollectionFiltered({
        collectionHandle,
        filters: Object.entries(selectedFilters).map(([facet, values]) => {
          return {
            facet: facet.split('.').reverse()[0] ?? facet,
            values,
          }
        }),
      })
    }
  }, [selectedFilters])

  return useMemo(() => {
    const result: ICollectionFilterQueryParamsContext = {
      clearAllFilters,
      selectedFilters,
      toggleSelectedFilter,
      updateFilterSelection,
      totalNumberOfSelectedFilterValues,
    }
    return result
  }, [
    clearAllFilters,
    selectedFilters,
    toggleSelectedFilter,
    updateFilterSelection,
    totalNumberOfSelectedFilterValues,
  ])
}
